import { useCallback, useEffect, useRef, useState, type CSSProperties, type TouchEvent } from "react";

/**
 * How far down the sheet must travel before letting go closes it.
 *
 * A quarter of its own height rather than a fixed distance: on a tall sheet a short drag is
 * plainly an accident, and the floor keeps a short one from closing on a twitch.
 */
const DISMISS_FRACTION = 0.25;
const DISMISS_FLOOR_PX = 120;
/** Pixels per millisecond. A quick flick should close even when it has not travelled far. */
const DISMISS_VELOCITY = 0.5;
/** Enough travel to prove a flick was meant for the sheet rather than a tap that wobbled. */
const FLICK_MIN_PX = 12;
const CLOSE_MS = 220;

export interface SheetDismiss {
	/** Attach to the scrolling sheet body: the gesture only starts when it is scrolled to the top. */
	bodyRef: React.RefObject<HTMLDivElement | null>;
	/** Drives the drag and the slide-out, so the caller does not repeat the mechanics. */
	style: CSSProperties;
	handlers: {
		onTouchStart: (event: TouchEvent) => void;
		onTouchEnd: () => void;
		onTouchCancel: () => void;
	};
}

/**
 * Drag a bottom sheet down to close it.
 *
 * The sheet keeps travelling after the finger lifts rather than vanishing where it was let go,
 * because a sheet that disappears mid-gesture reads as a glitch rather than a dismissal.
 */
export function useSheetDismiss(onDismiss: () => void): SheetDismiss {
	const bodyRef = useRef<HTMLDivElement>(null);
	const startYRef = useRef<number | null>(null);
	const lastRef = useRef({ y: 0, at: 0, velocity: 0 });
	// The sheet is zoomed by the text scale, so finger pixels and its own pixels differ.
	const zoomRef = useRef(1);
	const dismissAtRef = useRef(DISMISS_FLOOR_PX);
	const [offset, setOffset] = useState(0);
	const [closing, setClosing] = useState(false);

	const onTouchStart = useCallback((event: TouchEvent) => {
		const body = bodyRef.current;
		if (!body || body.scrollTop > 0) {
			startYRef.current = null;
			return;
		}
		const y = event.touches[0].clientY;
		startYRef.current = y;
		lastRef.current = { y, at: performance.now(), velocity: 0 };
		// Read our own variable rather than measuring: inside a `zoom`ed subtree
		// getBoundingClientRect reports the unzoomed box, so measuring always yields 1.
		const scale = Number.parseFloat(getComputedStyle(body).getPropertyValue("--ui-scale"));
		zoomRef.current = Number.isFinite(scale) && scale > 0 ? scale : 1;
		dismissAtRef.current = Math.max(DISMISS_FLOOR_PX, body.offsetHeight * DISMISS_FRACTION);
	}, []);

	// Registered by hand because React's listener cannot reliably preventDefault, and without
	// that the same drag scrolls the page behind the sheet.
	useEffect(() => {
		const body = bodyRef.current;
		if (!body) {
			return;
		}
		const onTouchMove = (event: globalThis.TouchEvent) => {
			if (startYRef.current == null) {
				return;
			}
			const y = event.touches[0].clientY;
			const travelled = y - startYRef.current;
			if (travelled <= 0) {
				setOffset(0);
				return;
			}
			event.preventDefault();

			const at = performance.now();
			const elapsed = at - lastRef.current.at;
			if (elapsed > 0) {
				lastRef.current.velocity = (y - lastRef.current.y) / elapsed;
			}
			lastRef.current.y = y;
			lastRef.current.at = at;
			setOffset(travelled / zoomRef.current);
		};

		body.addEventListener("touchmove", onTouchMove, { passive: false });
		return () => body.removeEventListener("touchmove", onTouchMove);
	}, []);

	const onTouchEnd = useCallback(() => {
		if (startYRef.current == null) {
			return;
		}
		startYRef.current = null;
		const flicked = lastRef.current.velocity >= DISMISS_VELOCITY && offset > FLICK_MIN_PX;
		if (offset >= dismissAtRef.current || flicked) {
			setClosing(true);
			return;
		}
		setOffset(0);
	}, [offset]);

	// Let the sheet finish sliding off the bottom before it is torn out of the tree.
	useEffect(() => {
		if (!closing) {
			return;
		}
		const timer = window.setTimeout(onDismiss, CLOSE_MS);
		return () => window.clearTimeout(timer);
	}, [closing, onDismiss]);

	let style: CSSProperties = {};
	if (closing) {
		style = { transform: "translateY(100%)", transition: `transform ${CLOSE_MS}ms ease-in` };
	} else if (offset > 0) {
		style = { transform: `translateY(${offset}px)`, transition: "none" };
	}

	return { bodyRef, style, handlers: { onTouchStart, onTouchEnd, onTouchCancel: onTouchEnd } };
}
